import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames/bind'

import CloseIcon from 'assets/svg/menu-close.inline.svg'
import { useScrollLock } from 'components/layout/ScrollLock'
import { CURSOR_ICONS } from 'components/ui/Cursor'
import CursorHover from 'components/ui/CursorHover'
import Portal from 'components/ui/Portal'
import useUIContext from 'context/ui'
import { Headline4, Subtitle } from 'components/ui/Typography'
import BriefContent from './BriefContent'
import { hasBrief } from './briefUtils'

import s from './BioLightbox.module.scss'
const cn = classNames.bind(s)

const EXIT_DURATION_MS = 400

const BioColumn = ({ content, columnClass }) => {
  if (!hasBrief(content)) return null

  return (
    <div className={cn('column', columnClass)}>
      <BriefContent content={content} multiline />
    </div>
  )
}

BioColumn.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  columnClass: PropTypes.string.isRequired,
}

const BioLightbox = ({ member, onClose }) => {
  const backdropRef = useRef(null)
  const panelRef = useRef(null)
  const closeButtonRef = useRef(null)
  const isClosingRef = useRef(false)
  const setCursorIcon = useUIContext((s) => s.setCursorIcon)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  useScrollLock(Boolean(member))

  useLayoutEffect(() => {
    if (!member?.originRect || !panelRef.current) return

    let { top, left, width, height } = member.originRect

    if (member.shape === 'rectangle') {
      const side = width / Math.SQRT2
      top += (height - side) / 2
      left += (width - side) / 2
      width = side
      height = side
    }

    const el = panelRef.current

    el.style.setProperty('--y', `${top}px`)
    el.style.setProperty('--x', `${left}px`)
    el.style.setProperty('--w', `${width}px`)
    el.style.setProperty('--h', `${height}px`)
  }, [member])

  useEffect(() => {
    isClosingRef.current = false
    setIsExiting(false)
    setIsExpanded(false)
    setIsVisible(false)

    if (!member) return

    let cancelled = false

    const frame1 = window.requestAnimationFrame(() => {
      if (cancelled) return

      window.requestAnimationFrame(() => {
        if (cancelled) return
        setIsExpanded(true)

        window.requestAnimationFrame(() => {
          if (cancelled) return
          setIsVisible(true)
          closeButtonRef.current?.focus()
        })
      })
    })

    return () => {
      cancelled = true
      window.cancelAnimationFrame(frame1)
    }
  }, [member])

  const handleClose = useCallback(() => {
    if (isClosingRef.current) return
    isClosingRef.current = true
    setCursorIcon(null)
    setIsVisible(false)
    setIsExiting(true)
  }, [setCursorIcon])

  useEffect(() => {
    if (!isExiting || !member) return

    let completed = false
    const finish = () => {
      if (completed) return
      completed = true
      onClose()
    }

    const onTransitionEnd = (e) => {
      if (e.target !== panelRef.current && e.target !== backdropRef.current) return
      if (e.propertyName === 'opacity' || e.propertyName === 'transform') finish()
    }

    const backdrop = backdropRef.current
    const panel = panelRef.current

    backdrop?.addEventListener('transitionend', onTransitionEnd)
    panel?.addEventListener('transitionend', onTransitionEnd)

    const timer = window.setTimeout(finish, EXIT_DURATION_MS)

    return () => {
      backdrop?.removeEventListener('transitionend', onTransitionEnd)
      panel?.removeEventListener('transitionend', onTransitionEnd)
      window.clearTimeout(timer)
    }
  }, [isExiting, member, onClose])

  useEffect(() => {
    if (!member) return

    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [member, handleClose])

  if (!member) return null

  const { name, position, shape, column1, column2 } = member

  return (
    <Portal root="lightbox">
      <div className={cn('wrapper', { isVisible, isExiting })}>
        <CursorHover icon={CURSOR_ICONS.CLOSE}>
          <div ref={backdropRef} className={cn('backdrop')} onClick={handleClose} aria-hidden="true" />
        </CursorHover>

        <div
          ref={panelRef}
          className={cn('panel', shape, { open: isExpanded, isExiting })}
          role="dialog"
          aria-modal="true"
          aria-label={name || 'Team member bio'}
        >
          <CursorHover icon={CURSOR_ICONS.CLOSE}>
            <button
              ref={closeButtonRef}
              className={cn('closeButton', { isVisible: isExpanded && !isExiting })}
              type="button"
              aria-label="Close"
              onClick={handleClose}
            >
              <CloseIcon />
            </button>
          </CursorHover>

          <div className={cn('content')}>
            {name && (
              <div className={cn('name')}>
                <Headline4>{name}</Headline4>
              </div>
            )}
            {position && (
              <div className={cn('position')}>
                <Subtitle as="h6">{position}</Subtitle>
              </div>
            )}
            {(hasBrief(column1) || hasBrief(column2)) && (
              <div className={cn('bio')}>
                <BioColumn content={column1} columnClass="columnFirst" />
                <BioColumn content={column2} columnClass="columnSecond" />
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  )
}

BioLightbox.propTypes = {
  member: PropTypes.shape({
    name: PropTypes.string,
    position: PropTypes.string,
    column1: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    column2: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    originRect: PropTypes.shape({
      top: PropTypes.number,
      left: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    shape: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
}

export default BioLightbox
